import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import api from "../../../api";
import CategorySaveFields from "./CategorySaveFields";

const DIFFICULTIES = ["Mixed", "Easy", "Medium", "Hard"];

/**
 * Generates a similar exam paper from an import: kicks off the async job, polls
 * it, then hands off to the Similar Paper review page. Modelled on
 * ConvertToMcqDialog — nothing reaches the question bank until the admin
 * approves questions in review.
 *
 * Props:
 *   open, onClose
 *   importId          — question bank import id
 *   title             — paper name, shown in the dialog
 *   questionCount     — how many questions the source paper has; also the default count
 *   defaultCategoryId — pre-selects the import's target category
 *   subject           — import's exam subject; the passage-source control is shown for
 *                       English papers (and when the subject is unknown)
 */
const GenerateSimilarPaperDialog = ({
  open,
  onClose,
  importId,
  title,
  questionCount,
  partCount,
  defaultCategoryId = "",
  subject = "",
}) => {
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const [count, setCount] = useState(questionCount || 10);
  const [difficulty, setDifficulty] = useState("Mixed");
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [shuffleOptions, setShuffleOptions] = useState(false);
  const [topicHint, setTopicHint] = useState("");
  const [categoryId, setCategoryId] = useState(defaultCategoryId || "");
  const [sourceType, setSourceType] = useState("ai_generated");

  const [passageBooks, setPassageBooks] = useState([]);
  const [passageBookId, setPassageBookId] = useState("");
  const [passageFromPage, setPassageFromPage] = useState("");
  const [passageToPage, setPassageToPage] = useState("");

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const maxCount = Math.max(Number(questionCount) || 10, 80);

  useEffect(() => {
    setCategoryId(defaultCategoryId || "");
  }, [defaultCategoryId, open]);

  useEffect(() => {
    if (open && questionCount) {
      setCount(Math.min(Number(questionCount) || 10, maxCount));
    }
  }, [questionCount, open, maxCount]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const showPassageSource = !subject || /english/i.test(String(subject));

  useEffect(() => {
    if (!open || !showPassageSource || passageBooks.length) return;
    api
      .get("/admin/master-form/reference-books-for-ai", { params: { subject: "english" } })
      .then((res) => setPassageBooks(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setPassageBooks([]));
  }, [open, showPassageSource, passageBooks.length]);

  const selectedBook = passageBooks.find((b) => b.id === passageBookId) || null;

  const stopPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(
          `/admin/question-bank/import/${importId}/similar-paper-status`,
          { params: { job_id: jobId } }
        );
        const job = res.data?.data;
        setProgress(job?.progress_percent || 0);

        if (job?.status === "completed") {
          stopPolling();
          setGenerating(false);
          onClose?.();
          navigate(`/admin/question-bank/similar-papers?job=${jobId}`);
        } else if (job?.status === "failed") {
          stopPolling();
          setGenerating(false);
          setError(job?.error_message || "Generation failed.");
        }
      } catch (err) {
        stopPolling();
        setGenerating(false);
        setError(err.response?.data?.message || "Failed to check generation status.");
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    setProgress(0);

    try {
      const res = await api.post(
        `/admin/question-bank/import/${importId}/generate-similar-paper`,
        {
          count: Math.min(Math.max(1, count), maxCount),
          difficulty,
          source: "ai",
          shuffle: shuffleQuestions,
          shuffle_options: shuffleOptions,
          topic_hint: topicHint.trim() || undefined,
          passage_book_id: passageBookId || undefined,
          passage_from_page: passageBookId && passageFromPage ? Number(passageFromPage) : undefined,
          passage_to_page: passageBookId && passageToPage ? Number(passageToPage) : undefined,
          target_source_type: sourceType,
          target_category_id: categoryId || null,
          async: true,
        }
      );

      const jobId = res.data?.data?.similar_paper_job_id;
      if (!jobId) throw new Error("No job id returned");
      startPolling(jobId);
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.message || "Failed to start similar paper generation.");
    }
  };

  const handleClose = () => {
    if (generating) return;
    setError("");
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        Generate Similar Paper
      </DialogTitle>

      <DialogContent dividers>
        {title && (
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
            {questionCount ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {questionCount} question{questionCount === 1 ? "" : "s"}
                {partCount && Number(partCount) > Number(questionCount)
                  ? ` · ${partCount} parts`
                  : ""}
              </Typography>
            ) : null}
          </Typography>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          AI writes a booklet-style paper mirroring this one&rsquo;s structure: subtle stem and
          value changes (not copies), with diagrams regenerated to match each source visual slot.
          Nothing is saved to the question bank until you review and approve.
        </Alert>

        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            type="number"
            label="Question count"
            value={count}
            onChange={(e) =>
              setCount(Math.max(1, Math.min(maxCount, Number(e.target.value) || 1)))
            }
            disabled={generating}
            inputProps={{ min: 1, max: maxCount }}
            helperText={
              questionCount
                ? `Source extract has ${questionCount}. Raise the count if the original paper is longer.`
                : `Max ${maxCount}`
            }
            sx={{ width: 150 }}
          />
          <TextField
            select
            size="small"
            label="Difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            disabled={generating}
            sx={{ minWidth: 140 }}
          >
            {DIFFICULTIES.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Topic focus (optional)"
            placeholder="e.g. Fractions, Ratio"
            value={topicHint}
            onChange={(e) => setTopicHint(e.target.value)}
            disabled={generating}
            sx={{ flex: "1 1 180px" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                checked={shuffleQuestions}
                onChange={(e) => setShuffleQuestions(e.target.checked)}
                disabled={generating}
              />
            }
            label={<Typography variant="body2">Shuffle question order</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={shuffleOptions}
                onChange={(e) => setShuffleOptions(e.target.checked)}
                disabled={generating}
              />
            }
            label={<Typography variant="body2">Shuffle MCQ option order</Typography>}
          />
        </Box>

        {showPassageSource && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "grey.50",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Reading passage source (English)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Leave on Automatic and the generator picks a book itself: first a matching
              reference book, then a public-domain title found online, and only then original
              prose in that book&rsquo;s style. Author and page are printed with the passage.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                select
                size="small"
                label="Book"
                value={passageBookId}
                onChange={(e) => {
                  setPassageBookId(e.target.value);
                  setPassageFromPage("");
                  setPassageToPage("");
                }}
                disabled={generating}
                sx={{ flex: "1 1 260px" }}
              >
                <MenuItem value="">Automatic</MenuItem>
                {passageBooks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.title}
                    {b.author ? ` — ${b.author}` : ""}
                  </MenuItem>
                ))}
              </TextField>

              {passageBookId ? (
                <>
                  <TextField
                    size="small"
                    type="number"
                    label="From page"
                    value={passageFromPage}
                    onChange={(e) => setPassageFromPage(e.target.value)}
                    disabled={generating}
                    inputProps={{ min: 1, max: selectedBook?.page_count || undefined }}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="To page"
                    value={passageToPage}
                    onChange={(e) => setPassageToPage(e.target.value)}
                    disabled={generating}
                    inputProps={{ min: 1, max: selectedBook?.page_count || undefined }}
                    helperText={
                      selectedBook?.page_count ? `${selectedBook.page_count} pages` : "Blank = auto"
                    }
                    sx={{ width: 130 }}
                  />
                </>
              ) : null}
            </Box>
          </Box>
        )}

        <CategorySaveFields
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          sourceType={sourceType}
          onSourceTypeChange={setSourceType}
          disabled={generating}
          helperText="You can change these again before saving to the question bank."
        />

        {generating && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Generating similar paper&hellip; {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={generating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
        >
          {generating ? "Generating…" : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateSimilarPaperDialog;
